// BetaSignup.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, message } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, ExperimentOutlined, LoginOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faDiscord } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import axios from 'axios';
import AppPageHeader from './components/AppPageHeader';
import LoadingCard from './components/LoadingCard';
import { LANGUAGE_CONFIG } from './LanguageConfig';
import './BetaSignup.css';

interface AccountProps {
  isAuth: boolean;
  usersSharedState: any;
  setUsersSharedState: any;
  showLoginModal: () => void;
}

const BetaSignup: React.FC<AccountProps> = ({ isAuth, usersSharedState, showLoginModal }) => {
  const [agreeToBeta, setAgreeToBeta] = useState(false);
  const [isBetaUser, setIsBetaUser] = useState(false);
  const [isLoadingBetaStatus, setIsLoadingBetaStatus] = useState(true);
  const [isSavingBetaStatus, setIsSavingBetaStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const betaLanguageText = LANGUAGE_CONFIG.betaLanguages.length
    ? LANGUAGE_CONFIG.betaLanguages.join(', ')
    : 'None at the moment.';
  const hasBetaLanguages = LANGUAGE_CONFIG.betaLanguages.length > 0;

  useEffect(() => {
    const checkBetaStatus = async () => {
      if (!isAuth || !usersSharedState.userID) {
        setIsLoadingBetaStatus(false);
        return;
      }

      setIsLoadingBetaStatus(true);
      setStatusError(null);

      try {
        const response = await axios.get(
          `https://askpoeai-nswdzfky7q-uc.a.run.app/check-beta-status/${usersSharedState.userID}`,
        );
        setIsBetaUser(Boolean(response.data.isBetaUser));
      } catch (error) {
        console.error('Error checking beta status:', error);
        setStatusError('Could not load your beta status. Try refreshing the page.');
      } finally {
        setIsLoadingBetaStatus(false);
      }
    };

    void checkBetaStatus();
  }, [isAuth, usersSharedState.userID]);

  const readiness = useMemo(() => {
    if (!isAuth) {
      return {
        tone: 'needed',
        label: 'Sign in needed',
        detail: 'Sign in to join or leave the beta program.',
      };
    }
    if (isLoadingBetaStatus) {
      return {
        tone: 'attention',
        label: 'Checking status',
        detail: 'Loading your current beta access.',
      };
    }
    if (statusError) {
      return {
        tone: 'blocked',
        label: 'Status unavailable',
        detail: statusError,
      };
    }
    if (isBetaUser) {
      return {
        tone: 'ready',
        label: 'Joined',
        detail: 'You currently have beta access enabled.',
      };
    }
    if (!agreeToBeta) {
      return {
        tone: 'needed',
        label: 'Agreement needed',
        detail: 'Review and accept the beta terms before joining.',
      };
    }
    return {
      tone: 'ready',
      label: 'Ready to join',
      detail: 'You can enable beta access now.',
    };
  }, [agreeToBeta, isAuth, isBetaUser, isLoadingBetaStatus, statusError]);

  const handleOptInOutBeta = async () => {
    if (!isAuth) {
      showLoginModal();
      return;
    }
    if (!isBetaUser && !agreeToBeta) {
      message.error('You must agree to the terms to join the beta program.');
      return;
    }

    setIsSavingBetaStatus(true);
    try {
      await axios.post(
        `https://askpoeai-nswdzfky7q-uc.a.run.app/opt-in-out-beta/${usersSharedState.userID}`,
        { betaAccess: !isBetaUser },
      );
      setIsBetaUser(!isBetaUser);
      setAgreeToBeta(false);
      message.success(`Successfully ${isBetaUser ? 'left' : 'joined'} the beta program.`);
    } catch (error) {
      console.error('Failed to update beta program status:', error);
      message.error('Error updating beta program status. Please try again later.');
    } finally {
      setIsSavingBetaStatus(false);
    }
  };

  const handleCheckboxChange = (event: CheckboxChangeEvent) => {
    setAgreeToBeta(event.target.checked);
  };

  return (
    <main className="beta-page-shell">
      <div className="beta-page-inner">
        <AppPageHeader
          kicker="Beta access"
          title="Beta Program"
          description={hasBetaLanguages
            ? 'Try early language features, review what is still in testing, and control your beta enrollment.'
            : 'Try preview features, review what is still in testing, and control your beta enrollment.'}
          metrics={
            <span className={`beta-readiness-pill is-${readiness.tone}`}>
              {readiness.label}
            </span>
          }
          actions={
            isAuth ? (
              <Button
                type={isBetaUser ? 'default' : 'primary'}
                danger={isBetaUser}
                icon={isBetaUser ? <ExclamationCircleOutlined /> : <ExperimentOutlined />}
                loading={isSavingBetaStatus}
                disabled={isLoadingBetaStatus || Boolean(statusError) || (!isBetaUser && !agreeToBeta)}
                onClick={handleOptInOutBeta}
              >
                {isBetaUser ? 'Leave Beta' : 'Join Beta'}
              </Button>
            ) : (
              <Button type="primary" icon={<LoginOutlined />} onClick={showLoginModal}>
                Sign in
              </Button>
            )
          }
          titleId="beta-page-title"
        />

        <section className="beta-workspace" aria-labelledby="beta-status-title">
          <div className="beta-section beta-section--status">
            <div className="beta-section-header">
              <div>
                <div className="beta-kicker">Current status</div>
                <h2 id="beta-status-title">Enrollment readiness</h2>
                <p>{readiness.detail}</p>
              </div>
              <span className={`beta-readiness-pill is-${readiness.tone}`}>{readiness.label}</span>
            </div>

            {isLoadingBetaStatus ? (
              <div className="beta-loading">
                <LoadingCard label="Loading beta status..." />
              </div>
            ) : (
              <div className="beta-status-grid">
                <div className="beta-status-row">
                  <CheckCircleOutlined aria-hidden="true" />
                  <div>
                    <strong>What changes</strong>
                    <span>
                      {isBetaUser
                        ? 'Leaving beta disables early access features for your account.'
                        : hasBetaLanguages
                          ? 'Joining beta enables access to supported beta languages.'
                          : 'Joining beta enables access to available preview features.'}
                    </span>
                  </div>
                </div>
                <div className="beta-status-row">
                  <CheckCircleOutlined aria-hidden="true" />
                  <div>
                    <strong>Credits</strong>
                    <span>New beta participants receive a one-time grant of 5,000 forever credits.</span>
                  </div>
                </div>
                <div className="beta-status-row">
                  <ExclamationCircleOutlined aria-hidden="true" />
                  <div>
                    <strong>Reliability</strong>
                    <span>Beta features are functional but may change as feedback and testing continue.</span>
                  </div>
                </div>
              </div>
            )}

            {!isBetaUser && isAuth && !isLoadingBetaStatus && !statusError && (
              <label className="beta-agreement-row">
                <Checkbox onChange={handleCheckboxChange} checked={agreeToBeta}>
                  I understand beta features may change and may be less stable than final features.
                </Checkbox>
              </label>
            )}
          </div>

          <section className="beta-section" aria-labelledby="beta-features-title">
            <div className="beta-section-header">
              <div>
                <div className="beta-kicker">Available beta features</div>
                <h2 id="beta-features-title">What you can test</h2>
                <p>This is the best place to send feedback if something feels unclear, slow, or hard to use.</p>
              </div>
            </div>

            <div className="beta-feature-grid">
              <div className="beta-feature-panel">
                <h3>{hasBetaLanguages ? 'Beta languages' : 'Preview features'}</h3>
                <p>{hasBetaLanguages
                  ? betaLanguageText
                  : 'All supported languages are generally available. Future preview features will appear here.'}</p>
              </div>
            </div>
          </section>

          <section className="beta-section" aria-labelledby="beta-faq-title">
            <div className="beta-section-header">
              <div>
                <div className="beta-kicker">FAQ</div>
                <h2 id="beta-faq-title">Before you join</h2>
              </div>
            </div>

            <div className="beta-faq-grid">
              <div className="beta-faq-item">
                <h3>What can I expect?</h3>
                <p>Beta participants get early access to new features that are still being tested and refined.</p>
              </div>
              <div className="beta-faq-item">
                <h3>Are beta features stable?</h3>
                <p>They are usable, but may have more edge cases than final features.</p>
              </div>
              <div className="beta-faq-item">
                <h3>How do I send feedback?</h3>
                <p>Email us or post in the Discord or Facebook community so the team can review it.</p>
              </div>
            </div>

            <div className="beta-contact-strip" aria-label="Feedback channels">
              <a className="beta-contact-chip" href="mailto:contact@scribeshadow.com">
                <FontAwesomeIcon icon={faEnvelope} />
                Email
              </a>
              <a className="beta-contact-chip" href="https://discord.com/invite/k7vnT38vPq" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faDiscord} />
                Discord
              </a>
              <a className="beta-contact-chip" href="https://www.facebook.com/groups/216351367578868/" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faFacebook} />
                Facebook
              </a>
            </div>
          </section>
        </section>

      </div>
    </main>
  );
};

export default BetaSignup;
